import { useState, useMemo, useEffect } from "react";
import { getEvalShortLabel } from "@mspca-beti/shared";
import { useResponsiveTickInterval } from "@/lib/chart-utils";
import { format, parseISO, addDays, differenceInDays } from "date-fns";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PawPrint, Building, ClipboardCheck, RefreshCw, TrendingUp, Grid3X3, Heart, Info, AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { CapacitySnapshot, SpeciesKennelMatrix, SpeciesEvalMatrix, SpeciesAdoptionMatrix } from "@/hooks/use-capacity-snapshots";

export type TrendMetric = "species" | "kennel" | "eval" | "adoption";
export type MatrixType = "speciesKennel" | "speciesEval" | "speciesAdoption";

const SPECIES_LABELS: Record<keyof SpeciesKennelMatrix, string> = {
  dogs: "Dogs",
  cats: "Cats",
  other: "Other",
};

const KENNEL_STATUS_LABELS: Record<string, string> = {
  available: "Available",
  unavail: "Unavailable",
  evaluation: "Evaluation",
  strayWait: "Stray Wait",
  unknown: "Unknown",
};

const SPECIES_EVAL_CAMEL_TO_CODE: Record<string, string> = {
  complete: "complete",
  needsReview: "needs_review",
  needsRecs: "needs_recs",
  unknown: "unknown",
  error: "error",
};
function getSpeciesEvalColHeader(key: string): string {
  if (key === "null") return "No Status";
  return getEvalShortLabel(SPECIES_EVAL_CAMEL_TO_CODE[key]);
}

const ADOPTION_LEVEL_LABELS: Record<string, string> = {
  l1: "Level 1",
  l2: "Level 2",
  l3: "Level 3",
  l4: "Level 4",
  null: "Not Assigned",
};

// Distinct colors for breakdown series - optimized for Dogs/Cats/Other visibility
const SERIES_COLORS = [
  "hsl(207, 90%, 54%)",  // Blue (Dogs)
  "hsl(25, 95%, 53%)",   // Orange (Cats) - clearly distinct from blue
  "hsl(142, 71%, 45%)",  // Green (Other) - clearly distinct from orange
  "hsl(280, 65%, 60%)",  // Purple
  "hsl(340, 75%, 55%)",  // Pink/Red
  "hsl(45, 93%, 47%)",   // Gold/Yellow
];

function formatPercent(value: number, total: number): string {
  if (total === 0) return "0%";
  return `${Math.round((value / total) * 100)}%`;
}

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), "M/d/yy");
  } catch {
    return dateStr;
  }
}

function formatFullDate(dateStr: string | null): string {
  if (!dateStr) return "Unknown";
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

interface BreakdownSummaryCardsProps {
  snapshot: CapacitySnapshot | null;
  isLoading?: boolean;
  speciesFilter: SpeciesFilter;
  onSpeciesFilterChange: (filter: SpeciesFilter) => void;
  isStale?: boolean;
}

function getFilteredBreakdownData(snapshot: CapacitySnapshot, speciesFilter: SpeciesFilter) {
  // For kennel status: sum from the matrix by species
  const kennelMatrix = snapshot.speciesKennelMatrix;
  // For eval status: sum from the matrix by species
  const evalMatrix = snapshot.speciesEvalMatrix;
  // For adoption level: sum from the matrix by species
  const adoptionMatrix = snapshot.speciesAdoptionMatrix;
  
  if (speciesFilter === "all") {
    // Use total counts
    return {
      total: snapshot.occupancyAllKennels,
      kennelData: [
        { label: "Available", value: snapshot.kennelStatusAvailable },
        { label: "Unavailable", value: snapshot.kennelStatusUnavail },
        { label: "Evaluation", value: snapshot.kennelStatusEvaluation },
        { label: "Stray Wait", value: snapshot.kennelStatusStrayWait },
        { label: "Unknown", value: snapshot.kennelStatusUnknown },
      ],
      evalData: [
        { label: "Complete", value: snapshot.evalStatusComplete },
        { label: "Needs Review", value: snapshot.evalStatusNeedsReview },
        { label: "Needs Recs", value: snapshot.evalStatusNeedsRecs },
        { label: "Unknown", value: snapshot.evalStatusUnknown },
        { label: "Error", value: snapshot.evalStatusError },
        { label: "No Status", value: snapshot.evalStatusNull },
      ],
      adoptionData: [
        { label: "Level 1", value: snapshot.adoptionLevelL1 },
        { label: "Level 2", value: snapshot.adoptionLevelL2 },
        { label: "Level 3", value: snapshot.adoptionLevelL3 },
        { label: "Level 4", value: snapshot.adoptionLevelL4 },
        { label: "Not Assigned", value: snapshot.adoptionLevelNull },
      ],
    };
  }
  
  // Filter by species
  const speciesKey = speciesFilter as keyof typeof kennelMatrix;
  const kennelRow = kennelMatrix[speciesKey];
  const evalRow = evalMatrix[speciesKey];
  const adoptionRow = adoptionMatrix[speciesKey];
  
  const speciesTotal = kennelRow.available + kennelRow.unavail + kennelRow.evaluation + kennelRow.strayWait + kennelRow.unknown;
  
  return {
    total: speciesTotal,
    kennelData: [
      { label: "Available", value: kennelRow.available },
      { label: "Unavailable", value: kennelRow.unavail },
      { label: "Evaluation", value: kennelRow.evaluation },
      { label: "Stray Wait", value: kennelRow.strayWait },
      { label: "Unknown", value: kennelRow.unknown },
    ],
    evalData: [
      { label: "Complete", value: evalRow.complete },
      { label: "Needs Review", value: evalRow.needsReview },
      { label: "Needs Recs", value: evalRow.needsRecs },
      { label: "Unknown", value: evalRow.unknown },
      { label: "Error", value: evalRow.error },
      { label: "No Status", value: evalRow.null },
    ],
    adoptionData: [
      { label: "Level 1", value: adoptionRow.l1 },
      { label: "Level 2", value: adoptionRow.l2 },
      { label: "Level 3", value: adoptionRow.l3 },
      { label: "Level 4", value: adoptionRow.l4 },
      { label: "Not Assigned", value: adoptionRow.null },
    ],
  };
}

export function BreakdownSummaryCards({ snapshot, isLoading, speciesFilter, onSpeciesFilterChange, isStale }: BreakdownSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-[140px]" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="breakdown-cards-skeleton">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="flex justify-between">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return null;
  }

  const { total, kennelData, evalData, adoptionData } = getFilteredBreakdownData(snapshot, speciesFilter);

  const speciesData = [
    { label: "Dogs", value: snapshot.occupancyDogs },
    { label: "Cats", value: snapshot.occupancyCats },
    { label: "Other", value: snapshot.occupancyOther },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-lg font-semibold" data-testid="text-population-breakdown-header">
          Population Breakdown
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Filter by species:</span>
          <Select value={speciesFilter} onValueChange={(v) => onSpeciesFilterChange(v as SpeciesFilter)}>
            <SelectTrigger className="w-[140px]" data-testid="select-species-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Species</SelectItem>
              <SelectItem value="dogs">Dogs</SelectItem>
              <SelectItem value="cats">Cats</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="breakdown-summary-cards">
        <Card data-testid="card-species-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <PawPrint className="h-4 w-4 text-muted-foreground" />
              Species
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {speciesFilter === "all" ? (
                speciesData.filter(item => item.value > 0).map((item) => (
                  <div key={item.label} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium" data-testid={`text-species-${item.label.toLowerCase()}-count`}>
                        {item.value}
                      </span>
                      <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-species-${item.label.toLowerCase()}-pct`}>
                        {formatPercent(item.value, snapshot.occupancyAllKennels)}
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground capitalize">{speciesFilter}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium" data-testid={`text-species-${speciesFilter}-count`}>
                      {total}
                    </span>
                    <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-species-${speciesFilter}-pct`}>
                      {formatPercent(total, snapshot.occupancyAllKennels)}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-kennel-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Building className="h-4 w-4 text-muted-foreground" />
              Kennel Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {kennelData.filter(item => item.value > 0).map((item) => (
                <div key={item.label} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium" data-testid={`text-kennel-${item.label.toLowerCase().replace(/\s+/g, '-')}-count`}>
                      {item.value}
                    </span>
                    <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-kennel-${item.label.toLowerCase().replace(/\s+/g, '-')}-pct`}>
                      {formatPercent(item.value, total)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-eval-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
              Behavior Eval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {evalData.filter(item => item.value > 0).map((item) => (
                <div key={item.label} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium" data-testid={`text-eval-${item.label.toLowerCase().replace(/\s+/g, '-')}-count`}>
                      {item.value}
                    </span>
                    <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-eval-${item.label.toLowerCase().replace(/\s+/g, '-')}-pct`}>
                      {formatPercent(item.value, total)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="card-adoption-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Heart className="h-4 w-4 text-muted-foreground" />
              Adoption Level
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {adoptionData.filter(item => item.value > 0).map((item) => (
                <div key={item.label} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium" data-testid={`text-adoption-${item.label.toLowerCase().replace(/\s+/g, '-')}-count`}>
                      {item.value}
                    </span>
                    <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-adoption-${item.label.toLowerCase().replace(/\s+/g, '-')}-pct`}>
                      {formatPercent(item.value, total)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface BreakdownSummaryCardsContentProps {
  snapshot: CapacitySnapshot | null;
  isLoading?: boolean;
  speciesFilter: SpeciesFilter;
  isStale?: boolean;
}

function BreakdownSummaryCardsContent({ snapshot, isLoading, speciesFilter, isStale }: BreakdownSummaryCardsContentProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="breakdown-cards-skeleton">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="flex justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!snapshot) {
    return null;
  }

  const { total, kennelData, evalData, adoptionData } = getFilteredBreakdownData(snapshot, speciesFilter);

  const speciesData = [
    { label: "Dogs", value: snapshot.occupancyDogs },
    { label: "Cats", value: snapshot.occupancyCats },
    { label: "Other", value: snapshot.occupancyOther },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="breakdown-summary-cards">
      <Card data-testid="card-species-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <PawPrint className="h-4 w-4 text-muted-foreground" />
            Species
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {speciesFilter === "all" ? (
              speciesData.filter(item => item.value > 0).map((item) => (
                <div key={item.label} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium" data-testid={`text-species-${item.label.toLowerCase()}-count`}>
                      {item.value}
                    </span>
                    <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-species-${item.label.toLowerCase()}-pct`}>
                      {formatPercent(item.value, snapshot.occupancyAllKennels)}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground capitalize">{speciesFilter}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium" data-testid={`text-species-${speciesFilter}-count`}>
                    {total}
                  </span>
                  <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-species-${speciesFilter}-pct`}>
                    {formatPercent(total, snapshot.occupancyAllKennels)}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-kennel-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Building className="h-4 w-4 text-muted-foreground" />
            Kennel Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {kennelData.filter(item => item.value > 0).map((item) => (
              <div key={item.label} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{item.label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium" data-testid={`text-kennel-${item.label.toLowerCase().replace(/\s+/g, '-')}-count`}>
                    {item.value}
                  </span>
                  <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-kennel-${item.label.toLowerCase().replace(/\s+/g, '-')}-pct`}>
                    {formatPercent(item.value, total)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-eval-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
            Behavior Eval
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {evalData.filter(item => item.value > 0).map((item) => (
              <div key={item.label} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{item.label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium" data-testid={`text-eval-${item.label.toLowerCase().replace(/\s+/g, '-')}-count`}>
                    {item.value}
                  </span>
                  <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-eval-${item.label.toLowerCase().replace(/\s+/g, '-')}-pct`}>
                    {formatPercent(item.value, total)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-adoption-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Heart className="h-4 w-4 text-muted-foreground" />
            Adoption Level
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {adoptionData.filter(item => item.value > 0).map((item) => (
              <div key={item.label} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{item.label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium" data-testid={`text-adoption-${item.label.toLowerCase().replace(/\s+/g, '-')}-count`}>
                    {item.value}
                  </span>
                  <Badge variant="secondary" className="text-xs min-w-[3rem] justify-center" data-testid={`badge-adoption-${item.label.toLowerCase().replace(/\s+/g, '-')}-pct`}>
                    {formatPercent(item.value, total)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface BreakdownTrendChartProps {
  snapshots: CapacitySnapshot[];
  metric: TrendMetric;
  days: number;
  isLoading?: boolean;
  /** Compact mode for side-by-side comparison - smaller height and legend */
  compact?: boolean;
}

interface TrendDataPoint {
  date: string;
  formattedDate: string;
  [key: string]: string | number;
}

function buildTrendData(snapshots: CapacitySnapshot[], metric: TrendMetric, days: number): TrendDataPoint[] {
  if (snapshots.length === 0) return [];
  
  // Create a map of snapshot dates for quick lookup
  const snapshotMap = new Map<string, CapacitySnapshot>();
  for (const s of snapshots) {
    snapshotMap.set(s.snapshotDate, s);
  }
  
  // Find the date range - use the latest snapshot date as end, go back `days` days
  const sortedDates = snapshots.map(s => s.snapshotDate).sort();
  const endDate = parseISO(sortedDates[sortedDates.length - 1]);
  const startDate = addDays(endDate, -(days - 1));
  
  // Build data points for each day in the range (consistent with other charts)
  const result: TrendDataPoint[] = [];
  let currentDate = startDate;
  
  while (differenceInDays(endDate, currentDate) >= 0) {
    const dateStr = format(currentDate, "yyyy-MM-dd");
    const s = snapshotMap.get(dateStr);
    
    const point: TrendDataPoint = {
      date: dateStr,
      formattedDate: formatDate(dateStr),
    };

    if (s) {
      if (metric === "species") {
        point.dogs = s.occupancyDogs;
        point.cats = s.occupancyCats;
        point.other = s.occupancyOther;
      } else if (metric === "kennel") {
        point.available = s.kennelStatusAvailable;
        point.unavail = s.kennelStatusUnavail;
        point.evaluation = s.kennelStatusEvaluation;
        point.strayWait = s.kennelStatusStrayWait;
        point.unknown = s.kennelStatusUnknown;
      } else if (metric === "eval") {
        point.complete = s.evalStatusComplete;
        point.needsReview = s.evalStatusNeedsReview;
        point.needsRecs = s.evalStatusNeedsRecs;
        point.unknown = s.evalStatusUnknown;
        point.error = s.evalStatusError;
        point.null = s.evalStatusNull;
      } else if (metric === "adoption") {
        point.l1 = s.adoptionLevelL1;
        point.l2 = s.adoptionLevelL2;
        point.l3 = s.adoptionLevelL3;
        point.l4 = s.adoptionLevelL4;
        point.notAssigned = s.adoptionLevelNull;
      }
    }
    // If no snapshot for this date, leave values undefined (will show as gap)

    result.push(point);
    currentDate = addDays(currentDate, 1);
  }
  
  return result;
}

interface BreakdownTooltipProps {
  active?: boolean;
  payload?: Array<{ 
    payload: TrendDataPoint;
    dataKey: string;
    value: number | null;
    stroke: string;
    name: string;
  }>;
  seriesKeys: { key: string; label: string }[];
  metric: TrendMetric;
}

function BreakdownTooltip({ active, payload, seriesKeys, metric }: BreakdownTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  
  // Check if this date has any data
  const hasAnyData = seriesKeys.some(s => {
    const val = data[s.key];
    return val !== undefined && val !== null;
  });

  // Calculate total for this date
  const total = seriesKeys.reduce((sum, s) => {
    const val = data[s.key];
    return sum + (typeof val === 'number' ? val : 0);
  }, 0);

  if (!hasAnyData) {
    return (
      <div className="rounded-lg border bg-background p-3 shadow-lg text-sm">
        <p className="font-medium mb-2">{formatFullDate(data.date)}</p>
        <p className="text-muted-foreground italic">No snapshot data for this date</p>
      </div>
    );
  }

  const metricLabel = metric === "species" ? "Population" 
    : metric === "kennel" ? "Kennel Status"
    : metric === "eval" ? "Behavior Eval Status"
    : "Adoption Level";

  return (
    <div className="rounded-lg border bg-background p-3 shadow-lg text-sm min-w-[180px]">
      <p className="font-medium mb-2">{formatFullDate(data.date)}</p>
      <div className="flex items-center justify-between gap-4 mb-2 pb-2 border-b">
        <span className="text-muted-foreground">{metricLabel}</span>
        <span className="font-semibold">{total.toLocaleString()}</span>
      </div>
      <div className="space-y-1">
        {seriesKeys.map((series, index) => {
          const val = data[series.key];
          const hasValue = val !== undefined && val !== null;
          const color = SERIES_COLORS[index % SERIES_COLORS.length];
          
          return (
            <div key={series.key} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: color }} 
                />
                <span className="text-muted-foreground">{series.label}</span>
              </div>
              <span className={hasValue ? "font-medium" : "text-muted-foreground italic text-xs"}>
                {hasValue ? (val as number).toLocaleString() : "N/A"}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getSeriesKeys(metric: TrendMetric): { key: string; label: string }[] {
  if (metric === "species") {
    return [
      { key: "dogs", label: "Dogs" },
      { key: "cats", label: "Cats" },
      { key: "other", label: "Other" },
    ];
  } else if (metric === "kennel") {
    return [
      { key: "available", label: "Available" },
      { key: "unavail", label: "Unavailable" },
      { key: "evaluation", label: "Evaluation" },
      { key: "strayWait", label: "Stray Wait" },
      { key: "unknown", label: "Unknown" },
    ];
  } else if (metric === "eval") {
    return [
      { key: "complete", label: "Complete" },
      { key: "needsReview", label: "Needs Review" },
      { key: "needsRecs", label: "Needs Recs" },
      { key: "unknown", label: "Unknown" },
      { key: "error", label: "Error" },
      { key: "null", label: "No Status" },
    ];
  } else {
    return [
      { key: "l1", label: "Level 1" },
      { key: "l2", label: "Level 2" },
      { key: "l3", label: "Level 3" },
      { key: "l4", label: "Level 4" },
      { key: "notAssigned", label: "Not Assigned" },
    ];
  }
}

export function BreakdownTrendChart({ snapshots, metric, days, isLoading, compact = false }: BreakdownTrendChartProps) {
  const data = useMemo(() => buildTrendData(snapshots, metric, days), [snapshots, metric, days]);
  const seriesKeys = useMemo(() => getSeriesKeys(metric), [metric]);
  
  // Track which series are visible - default all to visible
  const [visibleSeries, setVisibleSeries] = useState<Set<string>>(() => 
    new Set(seriesKeys.map(s => s.key))
  );
  
  // Reset visible series when metric changes
  useEffect(() => {
    setVisibleSeries(new Set(seriesKeys.map(s => s.key)));
  }, [metric]);
  
  const toggleSeries = (key: string) => {
    setVisibleSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Don't allow hiding all series
        if (next.size > 1) {
          next.delete(key);
        }
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const chartHeight = compact ? 200 : 300;
  
  if (isLoading) {
    return (
      <div className={`flex flex-col items-center justify-center border rounded-lg bg-muted/10`} style={{ height: chartHeight }} data-testid="trend-chart-loading">
        <RefreshCw className={`${compact ? 'h-5 w-5' : 'h-6 w-6'} text-muted-foreground animate-spin mb-2`} />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`flex items-center justify-center border rounded-lg bg-muted/20`} style={{ height: chartHeight }} data-testid="trend-chart-empty">
        <div className="text-center">
          <TrendingUp className={`${compact ? 'h-8 w-8' : 'h-10 w-10'} text-muted-foreground mx-auto mb-2`} />
          <p className="text-muted-foreground text-sm">No snapshot data available</p>
        </div>
      </div>
    );
  }

  const tickInterval = useResponsiveTickInterval(data.length);
  
  // Filter to only visible series
  const visibleSeriesKeys = seriesKeys.filter(s => visibleSeries.has(s.key));

  return (
    <div data-testid="breakdown-trend-chart">
      {/* Interactive Legend - fixed height container for alignment across charts */}
      <div className={`flex flex-wrap items-start ${compact ? 'gap-1 mb-2 min-h-[28px]' : 'gap-3 mb-4'}`} data-testid="chart-legend">
        {seriesKeys.map((series, index) => {
          const isVisible = visibleSeries.has(series.key);
          const color = SERIES_COLORS[index % SERIES_COLORS.length];
          return (
            <button
              key={series.key}
              onClick={() => toggleSeries(series.key)}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border transition-all cursor-pointer whitespace-nowrap ${
                compact ? 'text-xs h-6' : 'text-sm'
              } ${
                isVisible 
                  ? "border-border bg-background" 
                  : "border-muted bg-muted/30 opacity-50"
              }`}
              data-testid={`legend-toggle-${series.key}`}
            >
              <span 
                className={`inline-block rounded-sm flex-shrink-0 ${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'}`}
                style={{ 
                  backgroundColor: isVisible ? color : 'transparent',
                  border: `2px solid ${color}`
                }}
              />
              <span className={isVisible ? "" : "line-through"}>{series.label}</span>
            </button>
          );
        })}
      </div>
      
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart data={data} margin={{ top: 5, right: compact ? 5 : 30, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="formattedDate"
            tick={{ fontSize: compact ? 10 : 12 }}
            tickLine={false}
            axisLine={{ className: "stroke-muted" }}
            interval={tickInterval}
            angle={compact ? -45 : 0}
            textAnchor={compact ? "end" : "middle"}
            height={compact ? 50 : 30}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ className: "stroke-muted" }}
          />
          <RechartsTooltip
            content={<BreakdownTooltip seriesKeys={seriesKeys} metric={metric} />}
          />
          {visibleSeriesKeys.map((series) => {
            const originalIndex = seriesKeys.findIndex(s => s.key === series.key);
            return (
              <Line
                key={series.key}
                type="monotone"
                dataKey={series.key}
                name={series.label}
                stroke={SERIES_COLORS[originalIndex % SERIES_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 4 }}
                connectNulls={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface BreakdownMatrixViewProps {
  snapshot: CapacitySnapshot | null;
  matrixType: MatrixType;
  isLoading?: boolean;
}

export function BreakdownMatrixView({ snapshot, matrixType, isLoading }: BreakdownMatrixViewProps) {
  if (isLoading) {
    return (
      <div className="border rounded-lg p-4" data-testid="matrix-view-skeleton">
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="h-[200px] flex items-center justify-center border rounded-lg bg-muted/20" data-testid="matrix-view-empty">
        <div className="text-center">
          <Grid3X3 className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No snapshot data available</p>
        </div>
      </div>
    );
  }

  const speciesKeys: (keyof SpeciesKennelMatrix)[] = ["dogs", "cats", "other"];
  
  if (matrixType === "speciesKennel") {
    const matrix = snapshot.speciesKennelMatrix;
    const statusKeys = ["available", "unavail", "evaluation", "strayWait", "unknown"] as const;
    
    const rowTotals = speciesKeys.map((species) =>
      statusKeys.reduce((sum, status) => sum + matrix[species][status], 0)
    );
    const colTotals = statusKeys.map((status) =>
      speciesKeys.reduce((sum, species) => sum + matrix[species][status], 0)
    );
    const grandTotal = rowTotals.reduce((sum, val) => sum + val, 0);

    return (
      <div className="border rounded-lg overflow-hidden" data-testid="matrix-species-kennel">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Species</TableHead>
              {statusKeys.map((status) => (
                <TableHead key={status} className="text-center">
                  {KENNEL_STATUS_LABELS[status]}
                </TableHead>
              ))}
              <TableHead className="text-center font-semibold bg-muted/50">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {speciesKeys.map((species, rowIndex) => (
              <TableRow key={species}>
                <TableCell className="font-medium">{SPECIES_LABELS[species]}</TableCell>
                {statusKeys.map((status) => (
                  <TableCell key={status} className="text-center" data-testid={`cell-${species}-${status}`}>
                    {matrix[species][status]}
                  </TableCell>
                ))}
                <TableCell className="text-center font-semibold bg-muted/50" data-testid={`row-total-${species}`}>
                  {rowTotals[rowIndex]}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/50">
              <TableCell className="font-semibold">Total</TableCell>
              {colTotals.map((total, index) => (
                <TableCell key={statusKeys[index]} className="text-center font-semibold" data-testid={`col-total-${statusKeys[index]}`}>
                  {total}
                </TableCell>
              ))}
              <TableCell className="text-center font-bold" data-testid="grand-total">
                {grandTotal}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <div className="px-4 py-2 text-xs text-muted-foreground border-t bg-muted/20" data-testid="matrix-total-check">
          Total = {grandTotal} (occupancyAllKennels = {snapshot.occupancyAllKennels})
          {grandTotal === snapshot.occupancyAllKennels ? (
            <Badge variant="outline" className="ml-2 text-xs">Match</Badge>
          ) : (
            <Badge variant="destructive" className="ml-2 text-xs">Mismatch</Badge>
          )}
        </div>
      </div>
    );
  } else if (matrixType === "speciesEval") {
    const matrix = snapshot.speciesEvalMatrix;
    const statusKeys = ["complete", "needsReview", "needsRecs", "unknown", "error", "null"] as const;
    
    const rowTotals = speciesKeys.map((species) =>
      statusKeys.reduce((sum, status) => sum + matrix[species][status], 0)
    );
    const colTotals = statusKeys.map((status) =>
      speciesKeys.reduce((sum, species) => sum + matrix[species][status], 0)
    );
    const grandTotal = rowTotals.reduce((sum, val) => sum + val, 0);

    return (
      <div className="border rounded-lg overflow-hidden" data-testid="matrix-species-eval">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Species</TableHead>
              {statusKeys.map((status) => (
                <TableHead key={status} className="text-center">
                  {getSpeciesEvalColHeader(status)}
                </TableHead>
              ))}
              <TableHead className="text-center font-semibold bg-muted/50">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {speciesKeys.map((species, rowIndex) => (
              <TableRow key={species}>
                <TableCell className="font-medium">{SPECIES_LABELS[species]}</TableCell>
                {statusKeys.map((status) => (
                  <TableCell key={status} className="text-center" data-testid={`cell-${species}-${status}`}>
                    {matrix[species][status]}
                  </TableCell>
                ))}
                <TableCell className="text-center font-semibold bg-muted/50" data-testid={`row-total-${species}`}>
                  {rowTotals[rowIndex]}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/50">
              <TableCell className="font-semibold">Total</TableCell>
              {colTotals.map((total, index) => (
                <TableCell key={statusKeys[index]} className="text-center font-semibold" data-testid={`col-total-${statusKeys[index]}`}>
                  {total}
                </TableCell>
              ))}
              <TableCell className="text-center font-bold" data-testid="grand-total">
                {grandTotal}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <div className="px-4 py-2 text-xs text-muted-foreground border-t bg-muted/20" data-testid="matrix-total-check">
          Total = {grandTotal} (occupancyAllKennels = {snapshot.occupancyAllKennels})
          {grandTotal === snapshot.occupancyAllKennels ? (
            <Badge variant="outline" className="ml-2 text-xs">Match</Badge>
          ) : (
            <Badge variant="destructive" className="ml-2 text-xs">Mismatch</Badge>
          )}
        </div>
      </div>
    );
  } else {
    const matrix = snapshot.speciesAdoptionMatrix;
    const adoptionKeys = ["l1", "l2", "l3", "l4", "null"] as const;
    
    const rowTotals = speciesKeys.map((species) =>
      adoptionKeys.reduce((sum, level) => sum + matrix[species][level], 0)
    );
    const colTotals = adoptionKeys.map((level) =>
      speciesKeys.reduce((sum, species) => sum + matrix[species][level], 0)
    );
    const grandTotal = rowTotals.reduce((sum, val) => sum + val, 0);

    return (
      <div className="border rounded-lg overflow-hidden" data-testid="matrix-species-adoption">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Species</TableHead>
              {adoptionKeys.map((level) => (
                <TableHead key={level} className="text-center">
                  {ADOPTION_LEVEL_LABELS[level]}
                </TableHead>
              ))}
              <TableHead className="text-center font-semibold bg-muted/50">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {speciesKeys.map((species, rowIndex) => (
              <TableRow key={species}>
                <TableCell className="font-medium">{SPECIES_LABELS[species]}</TableCell>
                {adoptionKeys.map((level) => (
                  <TableCell key={level} className="text-center" data-testid={`cell-${species}-${level}`}>
                    {matrix[species][level]}
                  </TableCell>
                ))}
                <TableCell className="text-center font-semibold bg-muted/50" data-testid={`row-total-${species}`}>
                  {rowTotals[rowIndex]}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/50">
              <TableCell className="font-semibold">Total</TableCell>
              {colTotals.map((total, index) => (
                <TableCell key={adoptionKeys[index]} className="text-center font-semibold" data-testid={`col-total-${adoptionKeys[index]}`}>
                  {total}
                </TableCell>
              ))}
              <TableCell className="text-center font-bold" data-testid="grand-total">
                {grandTotal}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <div className="px-4 py-2 text-xs text-muted-foreground border-t bg-muted/20" data-testid="matrix-total-check">
          Total = {grandTotal} (occupancyAllKennels = {snapshot.occupancyAllKennels})
          {grandTotal === snapshot.occupancyAllKennels ? (
            <Badge variant="outline" className="ml-2 text-xs">Match</Badge>
          ) : (
            <Badge variant="destructive" className="ml-2 text-xs">Mismatch</Badge>
          )}
        </div>
      </div>
    );
  }
}

type BreakdownViewMode = "cards" | "matrix";

const MATRIX_TYPE_OPTIONS: { value: MatrixType; label: string }[] = [
  { value: "speciesKennel", label: "Species × Kennel" },
  { value: "speciesEval", label: "Species × Eval" },
  { value: "speciesAdoption", label: "Species × Adoption" },
];

interface PopulationBreakdownPanelProps {
  snapshots: CapacitySnapshot[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  isStale?: boolean;
  staleLocationNames?: string[];
}

export function PopulationBreakdownPanel({ snapshots, isLoading, isError, onRetry, isStale, staleLocationNames = [] }: PopulationBreakdownPanelProps) {
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");
  const [viewMode, setViewMode] = useState<BreakdownViewMode>("cards");
  const [matrixType, setMatrixType] = useState<MatrixType>("speciesKennel");

  const latestSnapshot = useMemo(() => {
    if (!snapshots || snapshots.length === 0) return null;
    // Find the most recent snapshot that has actual data (dataAsOf not null)
    const sortedSnapshots = [...snapshots].sort((a, b) => b.snapshotDate.localeCompare(a.snapshotDate));
    const snapshotWithData = sortedSnapshots.find(s => s.dataAsOf !== null);
    // Fall back to most recent if all are empty
    return snapshotWithData ?? sortedSnapshots[0];
  }, [snapshots]);

  if (isError) {
    return (
      <div data-testid="population-breakdown-panel">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-lg font-semibold" data-testid="text-population-breakdown-header">
            Population Breakdown
          </h2>
        </div>
        <Card>
          <CardContent className="py-8">
            <div className="text-center">
              <p className="text-muted-foreground mb-4">Couldn't load capacity snapshots.</p>
              <Button variant="outline" onClick={onRetry} data-testid="button-retry-breakdowns">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isLoading && snapshots.length === 0) {
    return (
      <div data-testid="population-breakdown-panel">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-lg font-semibold" data-testid="text-population-breakdown-header">
            Population Breakdown
          </h2>
        </div>
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground" data-testid="breakdowns-empty">
              No capacity snapshots found for this range.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="population-breakdown-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold" data-testid="text-population-breakdown-header">
            Population Breakdown
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <Select value={viewMode} onValueChange={(v) => setViewMode(v as BreakdownViewMode)}>
            <SelectTrigger className="w-[100px]" data-testid="select-breakdown-view-mode">
              <div className="flex items-center gap-2">
                {viewMode === "cards" ? (
                  <PawPrint className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <Grid3X3 className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cards">Cards</SelectItem>
              <SelectItem value="matrix">Matrix</SelectItem>
            </SelectContent>
          </Select>

          {/* Species filter (for cards view) or Matrix type (for matrix view) */}
          {viewMode === "cards" ? (
            <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
              <SelectTrigger className="w-[140px]" data-testid="select-species-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Species</SelectItem>
                <SelectItem value="dogs">Dogs</SelectItem>
                <SelectItem value="cats">Cats</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Select value={matrixType} onValueChange={(v) => setMatrixType(v as MatrixType)}>
              <SelectTrigger className="w-[170px]" data-testid="select-matrix-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MATRIX_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {viewMode === "cards" ? (
        <BreakdownSummaryCardsContent 
          snapshot={latestSnapshot} 
          isLoading={isLoading} 
          speciesFilter={speciesFilter}
          isStale={isStale}
        />
      ) : (
        <BreakdownMatrixView 
          snapshot={latestSnapshot} 
          matrixType={matrixType} 
          isLoading={isLoading} 
        />
      )}
    </div>
  );
}
